import { Avatar, Box, Divider, Grid2, styled, Typography } from "@mui/material";
import { ProfileAPI } from "hooks/ProfileAPI";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SETTING_TAB } from "store/slices/appSlice";
import { logout } from "store/slices/userSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { profileBorderColorRoleWise } from "utils/colorHelper";
import { AccountType, UserRoleTypes } from "utils/common";
import GKButton from "./GKButton";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BookmarkIcon,
  BranchIcon,
  CrmIcon,
  CustomizeColumnIcon,
  Edit1Icon,
  LabelIcon,
  LoanAmountIcon,
  LogoutIcon,
  RecurringIcon,
  SettingsIcon,
  UserIcon,
  WebsiteIcon,
} from "./Icons";
import { Show } from "./Show";

const CustomButton = styled(GKButton)(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: 8,
  "& .MuiButton-icon.MuiButton-endIcon": {
    marginLeft: "auto",
  },
}));

const ProfileMenu = ({ handleGetOPT }: any) => {
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const settingTab = useAppSelector((state) => state?.app?.settingTab);
  const userInfo = useAppSelector((state) => state?.user?.userData);

  const dispatch = useAppDispatch();

  const { handleUpdateProfileRole, handlePartnerCompany } = ProfileAPI();

  const [showAccountButtons, setShowAccountButtons] = useState(false);
  const [showPartnerList, setShowPartnerList] = useState(false);

  const items = [
    {
      name: "Advisor",
      isActive: UserRoleTypes.advisor === userInfo?.userRole,
      value: UserRoleTypes.advisor,
      show: userInfo?.isEmployee,
      click: () => {
        localStorage?.setItem("role", UserRoleTypes.advisor);
        handleUpdateProfileRole(UserRoleTypes.advisor);
      },
      icon: LoanAmountIcon,
      stroke: "1.5",
    },
    {
      name: "Investor",
      isActive: UserRoleTypes.client === userInfo?.userRole,
      value: UserRoleTypes.client,
      show: userInfo?.isClient,
      click: () => {
        localStorage?.setItem("role", UserRoleTypes.client);
        handleUpdateProfileRole(UserRoleTypes.client);
      },
      icon: CustomizeColumnIcon,
      stroke: "0.2",
    },
    {
      name: "Partner",
      isActive: UserRoleTypes.partner === userInfo?.userRole,
      show: userInfo?.isPartner,
      value: UserRoleTypes.partner,
      click: () => {
        setShowPartnerList(!showPartnerList);
      },
      icon: CrmIcon,
    },
  ];

  const buttonData = [
    {
      name: "User Profile",
      click: () => {
        dispatch(
          SETTING_TAB({
            type: "USER_PROFILE",
            name: "User Profile",
          }),
        );
        navigate("/app/user-profile/settings");
      },
      icon: UserIcon,
      isActive:
        settingTab.type === "USER_PROFILE" &&
        pathname === "/app/user-profile/settings",
      show: userInfo?.grantAccess,
      stroke: "1.5",
    },
    {
      name: "Company Profile",
      click: () => {
        dispatch(
          SETTING_TAB({
            name: "Company Profile",
            type: "COMPANY_PROFILE",
          }),
        );
        navigate("/app/user-profile/settings");
      },
      icon: LabelIcon,
      stroke: "1.5",
      isActive:
        settingTab.type === "COMPANY_PROFILE" &&
        pathname === "/app/user-profile/settings",
      show:
        userInfo?.userRole === UserRoleTypes.advisor && userInfo?.grantAccess,
    },
    {
      name: "My Website",
      click: () => {
        navigate("/app/my-website");
      },
      icon: WebsiteIcon,
      show:
        [AccountType.RA, AccountType.RA_RIA].includes(userInfo?.accountType) &&
        userInfo?.broker?.isRaAccess,
    },
    {
      name: "Change Password",
      click: () => {
        handleGetOPT();
      },
      icon: Edit1Icon,
      show:
        userInfo?.userRole === UserRoleTypes.advisor ||
        userInfo?.userRole === UserRoleTypes.client,
    },
    {
      name: "Settings",
      click: () => {
        dispatch(
          SETTING_TAB({
            name: settingTab?.name,
            type: settingTab?.type,
          }),
        );
        navigate("/app/user-profile/settings");
      },
      icon: SettingsIcon,
      stroke: "1.5",
      show:
        userInfo?.userRole === UserRoleTypes.advisor && userInfo?.grantAccess,
      isActive:
        settingTab.type === "PORTFOLIO_METRICS" &&
        pathname === "/app/user-profile/settings",
    },
    {
      name: "Company Master",
      click: () => navigate("/company-master/dashboard"),
      icon: BranchIcon,
      stroke: "0.1",
      show:
        userInfo?.userRole === UserRoleTypes.advisor && userInfo?.grantAccess,
      isActive:
        pathname === "/company-master/dashboard" ||
        pathname === "/company-master/setting",
    },
    {
      name: "Wallet",
      click: () => {
        navigate("/app/wallet");
      },
      icon: BookmarkIcon,
      stroke: "1.5",
      show:
        userInfo?.userRole === UserRoleTypes.advisor && userInfo?.grantAccess,
      isActive: pathname?.includes(`/app/wallet/`),
    },
  ];

  return (
    <Grid2 container>
      <Grid2 size={12} display={"flex"} alignItems={"center"}>
        <Box p={2}>
          <Avatar
            alt=""
            src={
              userInfo?.profilePic?.includes("s3") ? userInfo?.profilePic : ""
            }
            sx={{
              width: 40,
              height: 40,
              ".MuiAvatar-img": { borderRadius: "50%" },
              p: "2px",
              border: `2px solid ${profileBorderColorRoleWise(userInfo?.userRole)}`,
            }}
          />
        </Box>
        <Box>
          <Typography fontWeight={600} fontSize={14}>
            {userInfo?.name}
          </Typography>
          <Typography
            fontWeight={400}
            fontSize={12}
            color={"var(--neutral-500)"}
            display={"flex"}
          >
            {userInfo?.phone}
          </Typography>
        </Box>
      </Grid2>
      <Show.When isTrue={userInfo?.grantAccess}>
        <Grid2 size={12}>
          <Divider />
        </Grid2>
        {!showPartnerList && (
          <Grid2
            size={12}
            display={"flex"}
            onClick={() => {
              setShowAccountButtons(!showAccountButtons);
            }}
          >
            <CustomButton
              fullWidth
              sx={{
                padding: "16px",
                color: "var(--neutral-500)",
              }}
              startIcon={
                showAccountButtons ? (
                  <ArrowLeftIcon width={18} height={18} />
                ) : (
                  <RecurringIcon width={18} height={18} />
                )
              }
              endIcon={!showAccountButtons && <ArrowRightIcon width={18} />}
            >
              Switch Account
            </CustomButton>
          </Grid2>
        )}
        {showPartnerList && (
          <Grid2
            size={12}
            display={"flex"}
            onClick={() => {
              setShowPartnerList(!showPartnerList);
            }}
          >
            <CustomButton
              fullWidth
              sx={{ padding: "16px", color: "var(--neutral-500)" }}
              startIcon={
                showPartnerList ? (
                  <ArrowLeftIcon width={18} height={18} />
                ) : (
                  <RecurringIcon width={18} height={18} />
                )
              }
              endIcon={!showPartnerList && <ArrowRightIcon width={18} />}
            >
              Partners
            </CustomButton>
          </Grid2>
        )}
        <Grid2 size={12}>
          <Divider />
        </Grid2>
      </Show.When>
      <Grid2 size={12}>
        {!showPartnerList &&
          (showAccountButtons ? items : buttonData)?.map((dataItem) => {
            return (
              <Grid2 size={12} display={"flex"}>
                <CustomButton
                  fullWidth
                  key={dataItem.name}
                  onClick={dataItem?.click}
                  sx={{
                    display: dataItem?.show ? "flex" : "none",
                    color: dataItem?.isActive
                      ? `var(--primary-900)`
                      : "var(--neutral-500)",
                    px: "16px",
                  }}
                  startIcon={
                    <dataItem.icon
                      height={16}
                      width={16}
                      color={"var(--neutral-500)"}
                    />
                  }
                  endIcon={
                    dataItem?.show &&
                    dataItem.name === "Partner" && <ArrowRightIcon width={18} />
                  }
                >
                  {dataItem.name}
                </CustomButton>
              </Grid2>
            );
          })}

        {showPartnerList &&
          userInfo?.partnerAdvisors?.map(
            (dataItem: { name: string; id: number }) => {
              return (
                <CustomButton
                  fullWidth
                  key={dataItem.id}
                  onClick={() => {
                    localStorage?.setItem("role", UserRoleTypes.partner);
                    handlePartnerCompany(dataItem.id);
                  }}
                  sx={{
                    display: "flex",
                    color:
                      dataItem.id === userInfo?.partnerCompany
                        ? `var(--primary-900)`
                        : "var(--neutral-500)",
                    px: "16px",
                  }}
                >
                  {dataItem.name}
                </CustomButton>
              );
            },
          )}
      </Grid2>
      <Grid2 size={12}>
        <Divider />
        {!showAccountButtons && (
          <CustomButton
            fullWidth
            onClick={() => dispatch(logout())}
            sx={{ color: `${`var(--warning-700)`}`, padding: "16px" }}
            startIcon={<LogoutIcon width={16} height={16} />}
          >
            Logout
          </CustomButton>
        )}
      </Grid2>
    </Grid2>
  );
};

export default ProfileMenu;
